import { differenceInMinutes } from 'date-fns';
import { TodayTaskFlowDefinitionApi } from '@bridebook/toolbox/src/today-task/types';
import { PLACEHOLDER_TASK_DEFINITION } from '@bridebook/web/components/task/today-task/placeholder-task/task-definition';
import {
  TSessionDefinition,
  TTaskDefinitionUI,
  TTaskFlowDefinition,
  registeredTodayTasks,
} from '@bridebook/web/lib/task/task-flow-engine';
import { MOBILE_FLOW_TASK_IDS, MOBILE_TASKS_BY_ID, MobileFlowAssignment } from './config';

export const MOBILE_FLOW_IDS: Record<MobileFlowAssignment, string> = {
  venueNotSelected: 'e7f5749b-e81e-462a-a2d7-cacf83f8f851',
  venueSelected: 'e875e6d9-3b92-4df6-a32c-ad89c7ae3764',
};

const FLOW_ASSIGNMENT_BY_ID: Record<string, MobileFlowAssignment> = {
  [MOBILE_FLOW_IDS.venueSelected]: 'venueSelected',
  [MOBILE_FLOW_IDS.venueNotSelected]: 'venueNotSelected',
};

const DAY_DURATION_IN_MINS = 60 * 24;

const getTaskDefinition = (taskId: string): TTaskDefinitionUI | null =>
  registeredTodayTasks[taskId] ?? null;

const createTimedSession = ({
  id,
  taskId,
  sessionsToComplete,
}: {
  id: number;
  taskId: string;
  sessionsToComplete: number;
}): TSessionDefinition | null => {
  const taskDef = getTaskDefinition(taskId);
  if (!taskDef) return null;
  return {
    id,
    tasks: [taskDef],
    taskSelectionOrder: 'sequence',
    fallbackTask: PLACEHOLDER_TASK_DEFINITION,
    isSessionCompleted: ({ sessionStarted, sessionSignaled }) => {
      if (sessionSignaled) return true;
      if (!sessionStarted) return false;
      return (
        differenceInMinutes(Date.now(), sessionStarted) >= DAY_DURATION_IN_MINS * sessionsToComplete
      );
    },
  };
};

export const buildMobileFlowDefinition = (
  assignment: MobileFlowAssignment,
): TodayTaskFlowDefinitionApi => {
  // Note: mobile flow uses local config for task order/timing. Firestore provides tile copy/images only.
  const taskIds = MOBILE_FLOW_TASK_IDS[assignment];
  return {
    id: MOBILE_FLOW_IDS[assignment],
    isActive: true,
    // Wire-level string: Firestore flow name — do not rename without migration.
    name: `LIVE-25845_EnhanceMobileNavigation_${assignment}`,
    sessions: taskIds.map((taskId, index) => ({
      id: index + 1,
      tasks: [taskId],
    })),
    tasks: taskIds.reduce<TodayTaskFlowDefinitionApi['tasks']>((acc, taskId) => {
      const meta = MOBILE_TASKS_BY_ID[taskId];
      acc[taskId] = {
        id: taskId,
        name: meta?.title ?? 'Task',
        type: 'pre-configured',
      };
      return acc;
    }, {}),
  };
};

export const buildMobileFlowDefinitionUI = (flowId: string): TTaskFlowDefinition | null => {
  const assignment = FLOW_ASSIGNMENT_BY_ID[flowId];
  if (!assignment) return null;
  const taskIds = MOBILE_FLOW_TASK_IDS[assignment];
  const sessions = taskIds
    .map((taskId, index) =>
      createTimedSession({
        id: index + 1,
        taskId,
        sessionsToComplete: MOBILE_TASKS_BY_ID[taskId]?.sessionsToComplete ?? 1,
      }),
    )
    .filter((session): session is TSessionDefinition => session !== null);
  if (sessions.length === 0) return null;
  return { id: flowId, sessions };
};
